import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { logUnhandledError } from '../utils/logger';
import { reportError } from '../utils/errorReporting';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Strukturiertes Logging
    logUnhandledError(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      componentStack: errorInfo.componentStack,
    });

    // An Backend melden
    reportError(error, 'ErrorBoundary');
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-white/10 p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  Etwas ist schiefgelaufen
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Es ist ein unerwarteter Fehler aufgetreten
                </p>
              </div>
            </div>

            {/* Fehlermeldung */}
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-300 mb-3">
                Wir entschuldigen uns für die Unannehmlichkeiten. Sie können versuchen,
                die Seite neu zu laden oder zur Startseite zurückzukehren.
              </p>

              {/* Fehlerdetails (nur in Entwicklung) */}
              {import.meta.env.DEV && this.state.error && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Bug className="w-4 h-4" />
                    Fehlerdetails anzeigen
                  </summary>
                  <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono overflow-auto max-h-48">
                    <p className="text-red-600 dark:text-red-400 font-semibold">
                      {this.state.error.name}: {this.state.error.message}
                    </p>
                    {this.state.errorInfo && (
                      <pre className="mt-2 text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </div>

            {/* Aktionen */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 text-white dark:text-gray-900 rounded-md transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Seite neu laden
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md transition-colors"
              >
                <Home className="w-4 h-4" />
                Zur Startseite
              </button>
            </div>

            {/* Versuch erneut rendern */}
            <button
              onClick={this.handleReset}
              className="mt-4 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
            >
              Erneut versuchen ohne Neuladen
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook zum programmatischen Auslösen von Error Boundary
 */
export function useErrorHandler() {
  return (error: Error) => {
    throw error;
  };
}
